import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ProtectedRoutes from '../../auth/protected-routes';
import MainLayout from '../../layout/MainLayout';
import { ErrorBoundary } from 'react-error-boundary';
import MainErrorFallback from '../../components/errors/main';
import { logBoundaryError } from '../../components/errors/error-fallback';
import '../../scss/App.scss';

// Page-level code splitting: each route is its own chunk fetched on navigation,
// so the initial bundle no longer carries every page (and their heavy deps like
// the dashboard charts) up front. The app shell (ProtectedRoutes, MainLayout,
// error boundary) stays eager because it renders immediately.
const Login = lazy(() => import('../../features/auth/login/login'));
const NotFound = lazy(() => import('../../features/not-found/not-found'));
const Dashboard = lazy(() => import('../../features/dashboard/dashboard'));
const Events = lazy(() => import('../../features/events/events'));
const Theaters = lazy(() => import('../../features/theaters/theaters'));
const Forms = lazy(() => import('../../features/forms/forms'));
const Users = lazy(() => import('../../features/users/users'));
const Sessions = lazy(() => import('../../features/sessions/sessions'));
const CreateSession = lazy(
  () => import('../../features/sessions/components/create-update-session')
);
const FormBuilder = lazy(
  () => import('../../features/forms/components/form-builder/form-builder')
);
const AnswersTable = lazy(
  () => import('../../features/answers/answers-table')
);
const Profile = lazy(() => import('../../features/profile/profile'));
const Settings = lazy(() => import('../../features/settings/settings'));
const WhatsApp = lazy(() => import('../../features/whatsapp/whatsapp'));
const WhatsAppOrganizations = lazy(
  () => import('../../features/whatsapp/whatsapp-organizations')
);
const Guests = lazy(() => import('../../features/guests/guests'));
const SuperAdmin = lazy(() => import('../../features/super-admin/super-admin'));
const WhatsNew = lazy(() => import('../../features/whats-new/WhatsNew'));

const PageFallback = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      width: '100%',
    }}
  >
    <CircularProgress />
  </Box>
);

// Wrap a lazily-loaded page in a Suspense boundary with a shared fallback.
const page = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<PageFallback />}>
    <Component />
  </Suspense>
);

export const createRouter = createBrowserRouter([
  {
    path: '/',
    element: <ProtectedRoutes />,
    children: [
      {
        path: '/',
        element: (
          <ErrorBoundary
            FallbackComponent={MainErrorFallback}
            onError={logBoundaryError}
          >
            <MainLayout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: page(Events),
          },
          {
            path: '/report',
            element: page(Dashboard),
          },
          {
            path: '/report/:id',
            element: page(Dashboard),
          },
          {
            path: 'events',
            element: page(Events),
          },
          {
            path: 'events/sessions/:eventId',
            element: page(Sessions),
          },
          {
            path: 'events/:eventId/create-session',
            element: page(CreateSession),
          },
          {
            path: 'events/:sessionId/:eventId/edit-session',
            element: page(CreateSession),
          },
          {
            path: 'events/sessions/:sessionId/answers',
            element: page(AnswersTable),
          },
          {
            path: 'theaters',
            element: page(Theaters),
          },
          {
            path: 'forms',
            element: page(Forms),
          },
          {
            path: 'forms/form-builder',
            element: page(FormBuilder),
          },
          {
            path: 'forms/form-builder/:formId',
            element: page(FormBuilder),
          },
          {
            path: 'users',
            element: page(Users),
          },
          {
            path: 'profile',
            element: page(Profile),
          },
          {
            path: 'settings',
            element: page(Settings),
          },
          {
            path: 'organizations',
            element: page(WhatsAppOrganizations),
          },
          {
            path: 'whatsapp',
            element: page(WhatsApp),
          },
          {
            path: 'guests',
            element: page(Guests),
          },
          {
            path: 'super-admin',
            element: page(SuperAdmin),
          },
          {
            path: 'whats-new',
            element: page(WhatsNew),
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    element: page(Login),
  },
  {
    path: '*',
    element: page(NotFound),
  },
]);
